import { useMemo, memo } from "react";
import type { ToolUIPart } from "../../shared/types";
import { CardShell } from "./shared/CardShell";
import { InlineToolPanel } from "../InlineToolPanel";
import { parseSender, formatEmailTime, parseToolOutput } from "./shared/utils";

interface GmailMessage {
  from?: string;
  subject?: string;
  date?: string;
  snippet?: string;
  label_ids?: string[];
}

interface GmailThread {
  id: string;
  messages?: GmailMessage[];
  unread?: boolean;
  snippet?: string;
}

interface ParsedThreadList {
  ok: true;
  threads: GmailThread[];
  total?: number;
}

interface EmailListCardProps {
  part: ToolUIPart;
  onSendMessage: (text: string) => void;
}

export const EmailListCard = memo(function EmailListCard({ part, onSendMessage }: EmailListCardProps) {
  const isStreaming = part.state === "input-streaming";

  const result = useMemo(
    () => parseToolOutput<ParsedThreadList>(part.state === "output-available" ? part.output : undefined),
    [part.state, part.output],
  );

  // Fall back to raw panel on parse failure
  if (part.state === "output-available" && result.kind === "parse-failed") {
    return <InlineToolPanel part={part} />;
  }

  const isError = result.kind === "error";
  const threads = result.kind === "ok" ? result.data.threads : [];
  const total = result.kind === "ok" ? (result.data.total ?? threads.length) : 0;

  const accent = isError ? "error" : "email";
  const status = isStreaming ? "streaming" : isError ? "error" : "ready";
  const badge = isStreaming ? undefined : isError ? "error" : `${total} thread${total !== 1 ? "s" : ""}`;

  return (
    <CardShell
      icon="✉"
      title="Inbox"
      badge={badge}
      accent={accent}
      status={status}
    >
      {isStreaming && (
        <div className="tool-card__loading">Loading inbox...</div>
      )}
      {isError && (
        <div className="tool-card__error-msg">{result.error}</div>
      )}
      {!isStreaming && !isError && threads.length === 0 && (
        <div className="tool-card__empty">No threads found.</div>
      )}
      {!isStreaming && !isError && threads.map((thread) => {
        const firstMsg = thread.messages?.[0];
        const from = firstMsg?.from ?? "";
        const subject = firstMsg?.subject ?? "";
        const date = firstMsg?.date ?? "";
        const snippet = firstMsg?.snippet ?? thread.snippet ?? "";
        const senderName = from ? parseSender(from) : "";
        const timeStr = date ? formatEmailTime(date) : "";
        const isUnread = thread.unread ?? firstMsg?.label_ids?.includes("UNREAD") ?? false;

        return (
          <button
            key={thread.id}
            className={`tool-card__email-thread${isUnread ? " tool-card__email-thread--unread" : ""}`}
            onClick={() => onSendMessage(`Read email thread ${thread.id}`)}
            aria-label={senderName ? `${isUnread ? "Unread email" : "Email"} from ${senderName}: ${subject || snippet.slice(0, 40)}` : `${isUnread ? "Unread thread" : "Thread"}: ${snippet.slice(0, 40)}`}
            type="button"
          >
            <span
              className={isUnread ? "tool-card__unread-dot" : "tool-card__read-dot"}
              aria-hidden="true"
            />
            <span className="tool-card__thread-content">
              {senderName && (
                <span className={`tool-card__thread-sender${isUnread ? " tool-card__thread-sender--unread" : ""}`}>
                  {senderName}
                </span>
              )}
              {subject && (
                <span className="tool-card__thread-subject">{subject}</span>
              )}
              {snippet && (
                <span className="tool-card__thread-snippet">{snippet.slice(0, 100)}</span>
              )}
            </span>
            {timeStr && (
              <span className="tool-card__thread-time">{timeStr}</span>
            )}
          </button>
        );
      })}
    </CardShell>
  );
});
